import React, { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Wrench, Ticket, PalmtreeIcon, Users2, CreditCard, Phone, Cake, Bell, Calendar, Coffee, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";

/**
 * DagoverzichtWidget — 1-oogopslag samenvatting voor het dashboard.
 * Fetch /mijn-dagoverzicht en toon in 3 tabs: Vandaag / Morgen / Deze week.
 */

const iconVoor = (t) => ({
  werkbon: Wrench, ticket: Ticket, verlof: PalmtreeIcon, cred: CreditCard,
  klant: Phone, jub: Cake, mel: Bell,
}[t] || Bell);

const chipCls = "inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px]";

function Section({ icon: Icon, title, items, renderItem, colorClass = "text-slate-700", emptyText = "geen" }) {
  if (!items || items.length === 0) {
    return (
      <div className="border-l-2 border-slate-200 pl-3 mb-3">
        <div className={`flex items-center gap-1 text-xs font-semibold ${colorClass}`}>
          <Icon className="w-3 h-3" /> {title} <span className="text-slate-400">({emptyText})</span>
        </div>
      </div>
    );
  }
  return (
    <div className="border-l-2 border-blue-300 pl-3 mb-3">
      <div className={`flex items-center gap-1 text-xs font-semibold ${colorClass} mb-1`}>
        <Icon className="w-3 h-3" /> {title}
        <Badge variant="outline" className="text-[9px]">{items.length}</Badge>
      </div>
      <div className="space-y-1">{items.map(renderItem)}</div>
    </div>
  );
}

export default function DagoverzichtWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [audioBusy, setAudioBusy] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/mijn-dagoverzicht");
        setData(data);
      } catch { /* silent */ }
      setLoading(false);
    })();
  }, []);

  const speelAudio = async () => {
    setAudioBusy(true);
    try {
      const resp = await api.get("/digest/audio", { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "audio/mpeg" });
      const url = URL.createObjectURL(blob);
      setAudioUrl(url);
      const audio = new Audio(url);
      audio.play();
    } catch (e) { /* silent */ }
    finally { setAudioBusy(false); }
  };

  if (loading) {
    return <Card><CardContent className="p-4 text-sm text-slate-400 animate-pulse">Dagoverzicht laden…</CardContent></Card>;
  }
  if (!data) {
    return <Card><CardContent className="p-4 text-sm text-slate-400">Dagoverzicht niet beschikbaar</CardContent></Card>;
  }

  const v = data.vandaag;
  const m = data.morgen;
  const w = data.deze_week;

  return (
    <Card data-testid="dagoverzicht-widget">
      <CardContent className="p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-bold text-[#0D3B66] flex items-center gap-1">
            <Calendar className="w-4 h-4" /> Mijn dagoverzicht
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={speelAudio}
              disabled={audioBusy}
              className="flex items-center gap-1 bg-orange-50 hover:bg-orange-100 border border-orange-200 text-[#F26522] px-2 py-1 rounded text-[10px] font-semibold transition disabled:opacity-50"
              title="Ochtend-koffie stem-samenvatting"
              data-testid="dagoverzicht-audio-btn"
            >
              {audioBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Coffee className="w-3 h-3" />}
              Luister
            </button>
            <div className="text-[10px] text-slate-500">Hoi, {data.user_naam}</div>
          </div>
        </div>

        <Tabs defaultValue="vandaag">
          <TabsList className="grid grid-cols-3 h-8" data-testid="dagoverzicht-tabs">
            <TabsTrigger value="vandaag" className="text-xs" data-testid="tab-vandaag">
              Vandaag <Badge className="ml-1 h-4 text-[9px]" variant="secondary">{v.totaal}</Badge>
            </TabsTrigger>
            <TabsTrigger value="morgen" className="text-xs" data-testid="tab-morgen">
              Morgen <Badge className="ml-1 h-4 text-[9px]" variant="secondary">{m.totaal}</Badge>
            </TabsTrigger>
            <TabsTrigger value="week" className="text-xs" data-testid="tab-week">
              Deze week <Badge className="ml-1 h-4 text-[9px]" variant="secondary">{w.totaal}</Badge>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="vandaag" className="pt-3 space-y-1">
            <Section icon={Cake} title="Verjaardagen & jubilea" items={v.jubilea} colorClass="text-pink-700"
                     renderItem={(j, i) => (
                       <div key={i} className="text-xs">
                         {j.type === "verjaardag" ? "🎂" : "🎉"} <b>{j.employee}</b>
                         {j.leeftijd ? ` — ${j.leeftijd} jaar` : j.jaren ? ` — ${j.jaren} jaar in dienst` : ""}
                       </div>
                     )} />
            <Section icon={Wrench} title="Werkbonnen vandaag" items={v.werkbonnen} colorClass="text-blue-700"
                     renderItem={(w, i) => (
                       <Link key={i} to={w.url || "/werkbonnen"} className="text-xs block hover:underline">
                         <b>{w.nummer}</b> · {w.titel} · <span className="text-slate-500">{w.klant}</span>
                       </Link>
                     )} />
            <Section icon={Ticket} title="Open tickets" items={v.tickets} colorClass="text-amber-700"
                     renderItem={(t, i) => (
                       <Link key={i} to={t.url || "/tickets"} className="text-xs block hover:underline">
                         <b>#{t.nummer}</b> · {t.onderwerp}
                         {t.prioriteit === "hoog" && <span className="ml-1 text-red-600">●</span>}
                       </Link>
                     )} />
            <Section icon={PalmtreeIcon} title="Verlof te beoordelen" items={v.verlof_aanvragen}
                     colorClass="text-emerald-700"
                     renderItem={(vf, i) => (
                       <Link key={i} to={vf.url} className="text-xs block hover:underline">
                         {vf.employee_naam} — {vf.type} · {vf.vanaf}
                       </Link>
                     )} />
            <Section icon={CreditCard} title="Crediteur-goedkeuring" items={v.crediteur_te_verwerken}
                     colorClass="text-purple-700"
                     renderItem={(c, i) => (
                       <Link key={i} to={c.url} className="text-xs block hover:underline">
                         {c.leverancier} — € {c.bedrag}
                       </Link>
                     )} />
            <Section icon={Phone} title="Klanten om te bellen" items={v.vaste_klanten_bellen}
                     colorClass="text-cyan-700"
                     renderItem={(k, i) => (
                       <Link key={i} to={k.url} className="text-xs block hover:underline">
                         {k.naam} · {k.plaats} · <span className="text-slate-500">{k.status}</span>
                       </Link>
                     )} />
          </TabsContent>

          <TabsContent value="morgen" className="pt-3">
            <Section icon={Wrench} title="Werkbonnen morgen" items={m.werkbonnen} colorClass="text-blue-700"
                     renderItem={(w, i) => (
                       <Link key={i} to={w.url || "/werkbonnen"} className="text-xs block hover:underline">
                         <b>{w.nummer}</b> · {w.titel} · <span className="text-slate-500">{w.klant}</span>
                       </Link>
                     )} />
            {m.totaal === 0 && <div className="text-xs text-slate-400 italic">Geen items voor morgen.</div>}
          </TabsContent>

          <TabsContent value="week" className="pt-3">
            <Section icon={Wrench} title="Werkbonnen deze week" items={w.werkbonnen} colorClass="text-blue-700"
                     renderItem={(wb, i) => (
                       <Link key={i} to={wb.url || "/werkbonnen"} className="text-xs block hover:underline">
                         <b>{wb.nummer}</b> · {wb.datum?.slice(0, 10)} · {wb.titel}
                       </Link>
                     )} />
            {w.totaal === 0 && <div className="text-xs text-slate-400 italic">Geen items voor deze week.</div>}
          </TabsContent>
        </Tabs>

        {data.recente_meldingen && data.recente_meldingen.length > 0 && (
          <div className="border-t mt-3 pt-2">
            <div className="text-xs font-semibold text-slate-700 flex items-center gap-1 mb-1">
              <Bell className="w-3 h-3" /> Recente ontwikkelingen (24u)
            </div>
            <div className="space-y-0.5 max-h-32 overflow-y-auto">
              {data.recente_meldingen.slice(0, 6).map((n, i) => (
                <Link key={i} to={n.url || "/meldingen"} className="text-[11px] block hover:underline">
                  <span className={n.gelezen ? "text-slate-500" : "text-slate-800 font-medium"}>
                    {n.titel}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
